import { useEffect, useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL, IAddressInfoDto, IEmployeeViewDto } from '@/dtos';
import { useOrphanModeStore } from '@/modules/core/store';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';
import { ClaimAutoPayType, ClaimPayee, EmployeeAddress, EmployeeView } from '@/modules/user/user.types';

import { formatEmployeeViewData } from './formatData';

export const QUERY_KEY = 'getEmployeeView';

const useEmployeeViewDataQuery = (employeeId?: string) => {
  const { linkedEmployee } = useOrphanModeStore();
  const { setEmployee } = useEmployeeStore();
  const currentEmployeeId = linkedEmployee?.employeeId || employeeId;
  const { data, isLoading, isSuccess } = useQuery(
    [QUERY_KEY, API_URL.EMPLOYEES_EMPLOYEE_VIEW_BY_EMPLOYEE_ID(currentEmployeeId || '')],
    () => api.get<IEmployeeViewDto>(API_URL.EMPLOYEES_EMPLOYEE_VIEW_BY_EMPLOYEE_ID(currentEmployeeId || '')),
    {
      enabled: !!currentEmployeeId,
    }
  );
  const formattedData = useMemo(
    () => formatEmployeeViewData(data?.data, currentEmployeeId),
    [data, currentEmployeeId]
  );

  const formattedEmployee = useMemo<EmployeeView>(
    () => ({
      id: currentEmployeeId,
      personId: toString(data?.data?.person_id),
      firstName: data?.data?.first_name,
      hireOn: data?.data?.hire_on as unknown as string,
      lastName: data?.data?.last_name,
      preferredEmail: data?.data?.preferred_email,
      preferredName: data?.data?.preferred_name,
      employeeProvidedEmail: data?.data?.employee_provided_email,
      employerProvidedEmail: data?.data?.employer_provided_email,
      claimAutopayType: data?.data?.claim_autopay_type as unknown as ClaimAutoPayType,
      defaultClaimPayeeType: data?.data?.default_claim_payee_type as unknown as ClaimPayee,
      terminationDate: data?.data?.termination_date as unknown as string,
      isElectronicStatementOnly: data?.data?.is_electronic_statement_only,
      isCreatedByOrphan: data?.data?.is_created_by_orphan,
      isOrphaned: data?.data?.is_orphaned,
      isSsoUser: data?.data?.is_sso_user,
      isEmployeeEmailPreferred: data?.data?.is_employee_email_preferred,
      isVerifiedEmployeeEmail: data?.data?.is_verified_employee_email,
      isVerifiedEmployerEmail: data?.data?.is_verified_employer_email,
      isVerifiedPhoneNumber: data?.data?.is_verified_phone_number,
      isSmsNotificationEnabled: data?.data?.is_sms_notification_enabled,
      preferredPhone: {
        // TODO: this id is missing in DTO
        // @ts-ignore
        id: data?.data?.preferred_phone?.id,
        number: data?.data?.preferred_phone?.number,
        phoneType: data?.data?.preferred_phone?.phone_type,
        // TODO: this id is missing in DTO
        // @ts-ignore
        preferred: data?.data?.preferred_phone?.preferred,
        // TODO: this id is missing in DTO
        // @ts-ignore
        secondary: data?.data?.preferred_phone?.secondary,
      },
      phone: {
        number: data?.data?.phone?.number,
        phoneType: data?.data?.phone?.phone_type,
      },
      employerProvidedPhone: {
        number: data?.data?.employer_provided_phone?.number,
        phoneType: data?.data?.employer_provided_phone?.phone_type,
      },
      addresses: (data?.data?.addresses as unknown as IAddressInfoDto[])?.map((address) => {
        const item: EmployeeAddress = {
          id: address?.id || undefined,
          line1: address?.line1,
          line2: address?.line2 || undefined,
          city: address?.city,
          state: address?.state,
          zipcode: address?.zipcode,
          countryId: address?.country_id,
          addressType: address?.address_type,
          preferredMailing: address?.preferred_mailing || undefined,
          preferredPhysical: address?.preferred_physical || undefined,
          undeliverable: !!address?.undeliverable,
        };
        return item;
      }),
    }),
    [data, currentEmployeeId]
  );
  useEffect(() => {
    if (formattedData && isSuccess) {
      setEmployee(formattedEmployee);
    }
    // eslint-disable-next-line
  }, [formattedData]);
  return {
    formattedData,
    formattedEmployee,
    rawData: data?.data,
    isLoading,
    isSuccess,
  };
};

export default useEmployeeViewDataQuery;
